// src/components/Pentests/tabs/Retests.js
//
// Two-pane view: the frozen "Original engagement" snapshot card on top,
// then a chronological list of retests with each retest's own scoring.
// The parent PT's *live* scoring (top of page) is rolled up from the
// most recent retest by the backend; this tab is the operator's diff
// timeline of how findings have closed over the verification cycles.

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowUpRight, Award, Camera, History, RefreshCw } from 'lucide-react';
import { pentestsAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { EmptyState, PrimaryCTA } from '../../common/Shinobi';
import { StatusChip } from '../PentestStatus';
import { parseServerDate } from '../../../utils/time';

const GRADE_PILL = {
  A: 'bg-wasabi-100 text-wasabi-700 border-wasabi-300',
  B: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  C: 'bg-yellow-100 text-yellow-700 border-yellow-300',
  D: 'bg-orange-100 text-orange-700 border-orange-300',
  F: 'bg-red-100 text-red-700 border-red-300',
};

function fmtDate(ts) {
  if (!ts) return '-';
  const d = parseServerDate(ts);
  return d && !isNaN(d.getTime()) ? d.toLocaleDateString() : ts;
}

function GradeBadge({ grade, large = false }) {
  const cls = GRADE_PILL[grade] || GRADE_PILL.F;
  const sz = large ? 'w-12 h-12 text-2xl' : 'w-9 h-9 text-base';
  return (
    <span className={`inline-flex items-center justify-center rounded-lg font-bold border ${cls} ${sz}`}>
      {grade || '-'}
    </span>
  );
}

function SeverityRow({ counts, dim = false }) {
  if (!counts) return null;
  const cells = [
    ['Critical', counts.critical || 0, 'text-red-700'],
    ['High',     counts.high || 0,     'text-orange-700'],
    ['Medium',   counts.medium || 0,   'text-yellow-700'],
    ['Low',      counts.low || 0,      'text-blue-700'],
    ['Info',     counts.info || 0,     'text-slate-500'],
  ];
  return (
    <div className="flex items-center gap-3 flex-wrap">
      {cells.map(([label, n, color]) => (
        <div key={label} className={`flex items-center gap-1.5 ${dim ? 'opacity-70' : ''}`}>
          <span className={`text-xs uppercase tracking-wider text-slate-500`}>{label}</span>
          <span className={`font-mono font-semibold ${n ? color : 'text-slate-600'}`}>{n}</span>
        </div>
      ))}
    </div>
  );
}

// Frozen original-engagement snapshot. Renders only on parent PTs that have
// at least one retest; the data lives on the parent.original_* columns.
function OriginalSnapshotCard({ pentest }) {
  if (!pentest?.original_snapshot_at) return null;
  const at = parseServerDate(pentest.original_snapshot_at);
  return (
    <div className="rounded-2xl border border-cyber-300/40 bg-gradient-to-br from-cyber-50 to-cyber-100/40 p-5">
      <div className="flex items-start gap-4">
        <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl border border-purple-300 bg-purple-100 text-purple-700 shrink-0">
          <Camera className="w-5 h-5" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] text-purple-700 font-semibold mb-1">
            Original engagement (frozen)
          </div>
          <div className="text-sm text-slate-700 mb-3">
            Snapshot captured {at ? at.toLocaleString() : ''} - what this PT looked like at first delivery before any retests began.
          </div>
          <div className="flex items-center gap-5 flex-wrap">
            <div className="flex items-center gap-3">
              <GradeBadge grade={pentest.original_grade} large />
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500">Original grade</div>
                <div className="font-mono text-slate-800">
                  {pentest.original_overall_score != null ? Number(pentest.original_overall_score).toFixed(2) : '-'} / 5.00
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-wider text-slate-500">Findings</span>
              <span className="font-mono font-semibold text-slate-800">{pentest.original_finding_count ?? 0}</span>
            </div>
            <SeverityRow counts={pentest.original_severity_counts} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Retests({ pentest }) {
  const { id: pentestIdParam } = useParams();
  const pentestId = Number(pentestIdParam);
  const { isPentester } = useAuth();
  const navigate = useNavigate();

  const [retests, setRetests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);

  const isItselfARetest = pentest?.parent_pentest_id != null;

  const fetchAll = useCallback(async () => {
    try {
      const data = await pentestsAPI.retests.list(pentestId);
      setRetests(data || []);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [pentestId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const onStartRetest = async () => {
    setCreating(true);
    setError(null);
    try {
      const child = await pentestsAPI.retests.create(pentestId);
      navigate(`/pentests/${child.id}/pre-engagement`);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
      setCreating(false);
    }
  };

  if (loading) return <div className="text-slate-500">Loading retests...</div>;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Retests</h2>
          <p className="text-xs text-slate-500 mt-0.5 max-w-2xl">
            A retest clones this engagement's questionnaire and findings; each cloned finding starts as
            <span className="text-rose-700 font-semibold"> Still Open</span> until verified. The parent PT's live
            grade rolls up from the most recent retest, so the badge at the top of the page tracks
            verification progress.
          </p>
        </div>
        {isPentester && !isItselfARetest && (
          <PrimaryCTA
            variant="green"
            icon={RefreshCw}
            iconPosition="left"
            loading={creating}
            onClick={onStartRetest}
          >
            Start Retest
          </PrimaryCTA>
        )}
      </div>

      {isItselfARetest && (
        <div className="rounded-xl border border-cyber-300/40 bg-cyber-50/60 p-4 text-sm text-slate-700">
          This pentest is itself a retest. Retests-of-retests are mechanically possible but the operator
          UX prefers surfacing siblings under the original parent.
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-300 bg-red-100 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {!isItselfARetest && <OriginalSnapshotCard pentest={pentest} />}

      <div>
        <div className="flex items-center gap-2 mb-3">
          <History className="w-4 h-4 text-slate-500" />
          <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-slate-700">
            Retest timeline
          </h3>
          <span className="text-xs text-slate-500">
            {retests.length} {retests.length === 1 ? 'retest' : 'retests'}
          </span>
        </div>
        {retests.length === 0 ? (
          <EmptyState
            icon={RefreshCw}
            accent="green"
            title="No retests yet"
            description={
              isItselfARetest
                ? 'This is a retest. Retests of this retest are not surfaced here by default.'
                : isPentester
                  ? 'Click Start Retest to clone this engagement and verify the findings.'
                  : 'A retest will appear here when the Pentester creates one.'
            }
          />
        ) : (
          <div className="space-y-3">
            {retests.map((r, idx) => {
              const isLatest = idx === 0;
              return (
                <div
                  key={r.id}
                  onClick={() => navigate(`/pentests/${r.id}/dashboards`)}
                  className={`group cursor-pointer rounded-xl border p-4 transition ${
                    isLatest
                      ? 'border-wasabi-300 bg-shinobi-green/5 hover:border-shinobi-green/60'
                      : 'border-cyber-300/40 bg-cyber-50/60 hover:border-cyber-300/60 hover:bg-cyber-200/30'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <GradeBadge grade={r.grade} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center flex-wrap gap-2 mb-1">
                        <span className="text-sm font-semibold text-slate-900 truncate">{r.name}</span>
                        {isLatest && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border bg-wasabi-100 text-wasabi-700 border-wasabi-300 text-[9px] font-bold uppercase tracking-wider">
                            <Award className="w-3 h-3" />
                            Latest
                          </span>
                        )}
                        <StatusChip status={r.status} size="sm" />
                      </div>
                      <div className="flex items-center gap-4 flex-wrap text-xs">
                        <div className="flex items-center gap-1.5">
                          <span className="uppercase tracking-wider text-slate-500">Score</span>
                          <span className="font-mono font-semibold text-slate-800">
                            {r.overall_score != null ? Number(r.overall_score).toFixed(2) : '-'}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="uppercase tracking-wider text-slate-500">Open</span>
                          <span className="font-mono font-semibold text-slate-800">{r.finding_count}</span>
                        </div>
                        <SeverityRow counts={r.severity_counts} />
                        <div className="flex items-center gap-1.5">
                          <span className="uppercase tracking-wider text-slate-500">Created</span>
                          <span className="text-slate-500">{fmtDate(r.created_at)}</span>
                        </div>
                      </div>
                      {r.verification_progress && r.verification_progress.total > 0 && (
                        <div className="mt-2 flex items-center gap-4 flex-wrap text-xs">
                          <div className="flex items-center gap-1.5">
                            <span className="uppercase tracking-wider text-slate-500">Verified</span>
                            <span className="font-mono font-semibold text-emerald-700">
                              {r.verification_progress.verified} / {r.verification_progress.total}
                            </span>
                          </div>
                          <div className="flex-1 min-w-[120px] max-w-[260px] h-1.5 rounded-full bg-cyber-300/40 overflow-hidden">
                            <div
                              className="h-full bg-emerald-400/70"
                              style={{
                                width: `${Math.round(
                                  (100 * r.verification_progress.verified) /
                                    Math.max(1, r.verification_progress.total),
                                )}%`,
                              }}
                            />
                          </div>
                          {r.verification_progress.wont_fix > 0 && (
                            <span className="text-amber-700">
                              {r.verification_progress.wont_fix} won't fix
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-slate-500 group-hover:text-shinobi-green transition shrink-0" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
